// Section 19: Fever outbreak. 6% per season, Winter and Spring bias. 3 to 6 people fall ill with
// fever at once (the `outbreak` delta draws them from the event's own stream). Foreshadowed half
// the time. Choices: Quarantine (the sick stop working and stop spreading, -1 food each for
// isolation; the bodies phase holds it, Section 12) or Carry on.
// Constants: config.events['fever-outbreak'], config.eventDeck.foreshadowShare.
import type { Config } from '../../config';
import { choiceOutcome, eventEntry, eventPrompt } from '../../core/content-helpers';
import { contagiousCount } from '../../core/queries';
import { defineEvent, noModifiers, type EventChoiceDef } from '../../core/registry';

export function feverOutbreak(config: Config) {
  const c = config.events['fever-outbreak'];
  const choices: EventChoiceDef[] = [
    {
      id: 'quarantine',
      label: 'Quarantine the sick',
      preview: (ctx) => [
        {
          key: 'quarantine',
          params: {
            people: contagiousCount(ctx.state, ctx.cfg),
            food: contagiousCount(ctx.state, ctx.cfg) * c.quarantineFoodPerSick,
            seasons: c.quarantineSeasons,
          },
        },
      ],
      apply: (ctx, prompt) => [
        {
          kind: 'quarantine',
          active: true,
          untilTurn: ctx.season.turn + c.quarantineSeasons - 1,
        },
        choiceOutcome(prompt, 'quarantine', { seasons: c.quarantineSeasons }),
      ],
    },
    {
      id: 'carry-on',
      label: 'Carry on',
      preview: () => [{ key: 'carry-on', params: {} }],
      apply: (_ctx, prompt) => [choiceOutcome(prompt, 'carry-on')],
    },
  ];
  return defineEvent({
    id: 'fever-outbreak',
    label: 'Fever outbreak',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['winter', 'spring'],
    foreshadow: { chance: config.eventDeck.foreshadowShare },
    ledgerTag: null,
    weight: (ctx) => (Object.keys(ctx.state.people).length > 0 ? c.baseChance : 0),
    text: () => eventEntry('fever-outbreak', { min: c.minSick, max: c.maxSick }),
    apply: (ctx) => [
      {
        kind: 'outbreak',
        illnessId: 'fever',
        min: c.minSick,
        max: c.maxSick,
        source: 'event:fever-outbreak',
      },
      eventPrompt(ctx, 'fever-outbreak', choices, { defaultChoiceId: 'carry-on' }),
    ],
    choices,
    modifierTargets: [],
    modifiers: noModifiers,
  });
}
